import { useState, useEffect } from "react";
import { Download, FileText, X, ExternalLink } from "lucide-react";
import { UploadedFile } from "../../services/uploaded-files.service";
import Can from "../auth/Can";

interface FileItem {
  id: string;
  name: string;
  type: "file" | "folder";
  size: number;
  modified: string;
  extension?: string;
  thumbnail?: string;
  fileData?: UploadedFile;
}

interface PreviewModalProps {
  file: FileItem | null;
  onClose: () => void;
  formatFileSize: (bytes: number) => string;
}

const TEXT_EXTENSIONS = ["txt", "json", "log", "md", "xml", "yaml", "yml", "html", "htm", "js", "ts", "css"];
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

const PreviewModal = ({ file, onClose, formatFileSize }: PreviewModalProps) => {
  const [csvData, setCsvData] = useState<string[][] | null>(null);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);

  useEffect(() => {
    const ext = file?.extension?.toLowerCase();
    const url = file?.fileData?.url || file?.thumbnail;
    setCsvData(null);
    if (ext === "csv" && url) {
      fetchCsvData(url);
    }
  }, [file]);

  const fetchCsvData = async (url: string) => {
    try {
      setIsLoadingCsv(true);
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();

      if (!text || text.trim().length === 0) {
        throw new Error("Empty CSV file");
      }

      // Parse CSV with proper quote handling
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      const rows = lines.map(line => {
        const cells = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            cells.push(current.trim().replace(/^["']|["']$/g, ''));
            current = '';
          } else {
            current += char;
          }
        }
        cells.push(current.trim().replace(/^["']|["']$/g, ''));
        return cells;
      });

      const validRows = rows.filter(row => row.some(cell => cell.length > 0));

      if (validRows.length > 0) {
        setCsvData(validRows);
      } else {
        throw new Error("No valid data in CSV");
      }
    } catch (error) {
      console.error("Error fetching CSV:", error);
      setCsvData(null);
    } finally {
      setIsLoadingCsv(false);
    }
  };

  if (!file) return null;

  const renderPreview = () => {
    const extension = file.extension?.toLowerCase();
    const fileUrl = file.fileData?.url || file.thumbnail;

    // PDF Preview
    if (extension === "pdf" && fileUrl) {
      return (
        <iframe
          src={fileUrl}
          className="w-full h-full border-0"
          title={file.name}
        />
      );
    }

    // CSV Preview
    if (extension === "csv") {
      if (isLoadingCsv) {
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading CSV preview...</p>
            </div>
          </div>
        );
      }

      if (csvData && csvData.length > 0) {
        return (
          <div className="w-full h-full overflow-auto p-4 bg-gradient-to-br from-green-50/30 to-emerald-50/30">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-green-50 to-emerald-50 sticky top-0 z-10">
                    <tr>
                      {csvData[0]?.map((header, index) => (
                        <th
                          key={index}
                          className="px-6 py-3 text-left text-xs font-bold text-green-900 uppercase tracking-wider whitespace-nowrap border-r border-green-100 last:border-r-0"
                        >
                          {header || `Column ${index + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {csvData.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-green-50/50 transition-colors">
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap border-r border-gray-100 last:border-r-0"
                          >
                            {cell || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-4 text-center text-sm text-gray-500">
              Showing {csvData.length > 1 ? csvData.length - 1 : 0} rows
            </div>
          </div>
        );
      }

      // Fallback if CSV failed to load
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <FileText className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Failed to load CSV preview</p>
            {fileUrl && (
              <Can permission="documents:download">
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </a>
              </Can>
            )}
          </div>
        </div>
      );
    }

    // Office docs (Word / Excel / PowerPoint) — Microsoft Office Online viewer.
    // Requires a publicly reachable URL (Spaces public objects qualify).
    if (
      ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(extension || "") &&
      fileUrl
    ) {
      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1 overflow-hidden">
            <iframe
              src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
              className="w-full h-full border-0"
              title={file.name}
            />
          </div>
          <div className="bg-blue-50 border-t border-blue-200 p-3">
            <div className="flex items-center justify-between max-w-6xl mx-auto">
              <p className="text-sm text-blue-800">
                Preview powered by Microsoft Office Online
              </p>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                Open Original
              </a>
            </div>
          </div>
        </div>
      );
    }

    // Image Preview
    if (extension && IMAGE_EXTENSIONS.includes(extension) && fileUrl) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gray-50 p-4 overflow-auto">
          <img src={fileUrl} alt={file.name} className="max-w-full max-h-full object-contain" />
        </div>
      );
    }

    // Text / JSON / code Preview — render via iframe (browser navigation is
    // not blocked by CORS, unlike fetch() against the storage bucket).
    if (extension && TEXT_EXTENSIONS.includes(extension) && fileUrl) {
      return (
        <iframe
          src={fileUrl}
          className="w-full h-full border-0 bg-white"
          title={file.name}
        />
      );
    }

    // Fallback - No preview available
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No preview available</p>
          {fileUrl && (
            <Can permission="documents:download">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download File
            </a>
            </Can>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {file.name}
              </h3>
              <p className="text-sm text-gray-500">
                {file.type === "file" ? formatFileSize(file.size) : "Folder"} •
                Modified {file.modified}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(file.fileData?.url || file.thumbnail) && (
              <Can permission="documents:download">
              <a
                href={file.fileData?.url || file.thumbnail}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                download
              >
                <Download className="w-5 h-5" />
              </a>
              </Can>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden bg-gray-100">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
